/**
 * Epic 9 — pre-flight kontrola driftu/konfliktů a finální diff pro change
 * script. Staví na `sync.ts` (Epic 2, `buildSyncDiff`/`diffItemKey`) a na
 * `diff.ts`'s `detectRenames` (Epic 9) — žádná paralelní diff logika.
 *
 * Tři zdroje pravdy (epic-9 kap. 6): `baseline` (poslední známý stav DB,
 * `findBaseline()` v backendu), `currentDb` (čerstvý re-scan, E9-US1) a
 * `model` (aktuální pracovní schéma). `dbDrift = baseline vs. currentDb`
 * (přesně stejná sémantika jako Epic 2's `buildSyncDiff`), `userDiff =
 * baseline vs. model` (s `detectRenames`, aby se přejmenování neplést s
 * remove+add). Konflikt = položka, která se změnila na OBOU stranách
 * (stejný `objectType`+`path` v obou diffech, epic-9 kap. 8).
 */
import { compareSchemaModels } from './diff.js';
import { buildSyncDiff, defaultSyncItemSelection, diffItemKey } from './sync.js';
/**
 * E9-US1/US2 — pre-flight: DB drift (baseline vs. čerstvý re-scan) + detekce
 * konfliktů s rozpracovanou uživatelovou změnou (baseline vs. model,
 * rename-aware). Čistá funkce — volá se na klientovi, stejně jako
 * `buildSyncDiff`/`buildSyncMergePlan` v Epic 2.
 */
export function buildDriftCheck(baseline, currentDb, model) {
    const driftItems = buildSyncDiff(baseline, currentDb);
    const userDiff = compareSchemaModels(baseline, model, { detectRenames: true });
    const userByKey = new Map(userDiff.map((item) => [`${item.objectType}:${item.path}`, item]));
    const conflicts = [];
    for (const drift of driftItems) {
        const userItem = userByKey.get(`${drift.objectType}:${drift.path}`);
        if (!userItem)
            continue;
        conflicts.push({
            key: diffItemKey(drift),
            objectType: drift.objectType,
            path: drift.path,
            baselineValue: drift.oldValue,
            dbValue: drift.newValue,
            modelValue: userItem.newValue,
            resolution: 'unresolved',
        });
    }
    return { hasDrift: driftItems.length > 0, driftItems, conflicts };
}
/**
 * E9-US4 — finální diff pro granulární výběr položek change scriptu:
 * `baseline vs. model`, rename-aware. Volá se AŽ PO dořešení pre-flightu
 * (E9-US1 až US3), kdy `baseline` už odpovídá aktuálnímu stavu DB (viz
 * `doc/epic-9-generovani-change-skriptu.md` kap. 8 — "Volba Zachovat mou
 * změnu... Baseline se pro danou položku posune na aktuální DB hodnotu").
 */
export function buildChangeScriptDiff(baseline, model) {
    return compareSchemaModels(baseline, model, { detectRenames: true }).map((item) => ({
        ...item,
        selected: defaultSyncItemSelection(item),
    }));
}
//# sourceMappingURL=changeScript.js.map